import React, { useState } from "react";
import { SubscriptionService } from "../../services/subscription.service";
import { Switch, Collapse } from "@mui/material";
import { CheckCircle, RemoveCircle, Cancel, Lock, ExpandMore, ExpandLess } from "@mui/icons-material";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import moment from "moment";

import { formatLimitValue } from "../../pages/superadmin/planLimits.util";
import { ActionButton, CardLayout } from "../v3";
import { Chip, Stack as MuiStack } from "@mui/material";
import { ToggleButton } from "../v3";

const LIMIT_LABELS = {
  maxUsers: "Users",
  maxProjects: "Projects",
  maxClients: "Clients",
  maxEmployees: "Employees",
  maxWorkers: "Workers",
  maxVendors: "Vendors",
  maxSubcontractors: "Subcontractors",
  maxAccounts: "Accounts",
  maxStorageMB: "Storage (MB)",
  allowProjectFullReport: "Project Full Report",
  allowClientFullReport: "Client Full Report",
  maxReportRangeMonths: "Report date range",
};

export const PlanDetails = ({ planDetails = {}, modules = [], onClick = () => {} }) => {
  const [active, setActive] = useState(planDetails?.active);
  const [loading, setLoading] = useState(false);
  const [isExpired] = useState(planDetails?.endDate ? moment().isAfter(moment(planDetails.endDate)) : true);
  const [isExpiringSoon] = useState(!isExpired && moment(planDetails.endDate).diff(moment(), "days") <= 10);
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    setActive(planDetails?.active);
  }, [planDetails?.active]);

  const handleToggle = () => {
    const newStatus = !active;
    setLoading(true);
    SubscriptionService.updatePlan(planDetails.id, { active: newStatus })
      .then(() => {
        setActive(newStatus);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const formatFeatureName = (name = "") => {
    return name
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const activeModules = modules.filter((f) => f.active);
  const inactiveModules = modules.filter((f) => !f.active);

  return (
    <CardLayout sx={{ margin: "0px 11px 11px 11px" }}>
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#7B61FF" }}>
            {planDetails?.name}
          </Typography>
          {planDetails?.type && (
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 600,
                px: 1.2,
                py: 0.4,
                borderRadius: "6px",
                backgroundColor: "#EEE8FF",
                color: "#7B61FF",
              }}
            >
              {planDetails?.type}
            </Box>
          )}
        </Box>

        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
            <Box display="flex" alignItems="baseline" gap={1}>
              <Typography variant="h4" fontWeight={700}>
                {`₹ ${planDetails?.price}`}
              </Typography>
              <Typography variant="caption">{planDetails?.billingCycle === "MONTHLY" ? "/mo" : "/yr"}</Typography>
            </Box>
            <Typography variant="caption" sx={{ lineHeight: 1.2, display: "block", mt: 0.5 }}>
              {planDetails?.startDate && planDetails?.endDate
                ? `${moment(planDetails?.startDate).format("DD MMM YYYY")} to ${moment(planDetails?.endDate).format("DD MMM YYYY")}`
                : ""}
            </Typography>
            {planDetails?.endDate && (
              <Chip
                label={(() => {
                  return isExpired
                    ? `Expired ${moment(planDetails.endDate).fromNow(true)} ago`
                    : `Expires ${moment(planDetails.endDate).fromNow()}`;
                })()}
                size="small"
                sx={{
                  fontWeight: 600,
                  backgroundColor: isExpired ? "#FDECEA" : "#E6F4EA",
                  color: isExpired ? "#B71C1C" : isExpiringSoon ? "#FF9800" : "#1B5E20",
                  mt: 0.5,
                }}
              />
            )}
          </Box>
          <MuiStack direction="row" spacing={1} alignItems="center" mt={0.5}>
            <Chip
              label={active ? "Active" : "Inactive"}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: active ? "#E6F4EA" : "#FDECEA",
                color: active ? "#1B5E20" : "#B71C1C",
              }}
            />
            <ToggleButton checked={!!active} onToggleChange={handleToggle} disabled={loading} />
          </MuiStack>
        </Box>

        <Typography variant="body2" color="text.secondary" lineHeight={3}>
          {planDetails?.description}
        </Typography>

        {/* Limits Section */}
        {activeModules.some((f) => f.limits && Object.keys(f.limits).length > 0) && (
          <Box mt={1}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#7B61FF" }}>
              Resource Limits
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {activeModules.map((feature) => {
                if (!feature.limits || Object.keys(feature.limits).length === 0) return null;
                const limitEntries = Object.entries(feature.limits).filter(
                  ([key]) => key !== "includedFeatureCodes" && key !== "hiddenFeatureCodes",
                );
                if (limitEntries.length === 0) return null;
                return (
                  <Box key={feature.id} sx={{ mb: 0.5 }}>
                    {limitEntries.map(([key, value]) => (
                      <Chip
                        key={key}
                        label={`${LIMIT_LABELS[key] || key}: ${formatLimitValue(key, value)}`}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          backgroundColor: "#EEE8FF",
                          color: "#7B61FF",
                          mr: 0.5,
                          mb: 0.5,
                        }}
                      />
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Features Section */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#7B61FF" }}>
            Modules & Features
          </Typography>

          <Stack spacing={0.5} mt={0.5}>
            {activeModules.map((feature) => {
              const includedCodes = feature.limits?.includedFeatureCodes || [];
              const hiddenCodes = feature.limits?.hiddenFeatureCodes || [];
              const childFeatures = feature.feature?.children || [];
              const includedChildren = childFeatures.filter(
                (c) => includedCodes.includes(c.code) && !hiddenCodes.includes(c.code),
              );
              const excludedChildren = childFeatures.filter(
                (c) => !includedCodes.includes(c.code) && !hiddenCodes.includes(c.code),
              );
              const hiddenChildren = childFeatures.filter((c) => hiddenCodes.includes(c.code));
              const totalChildren = childFeatures.length;
              const totalExcluded = excludedChildren.length;
              const totalIncluded = includedChildren.length;
              const totalHidden = hiddenChildren.length;
              const hasFeatures = totalChildren > 0;
              const hasExclusionsOrLocked = totalExcluded > 0 || totalHidden > 0;
              const moduleKey = feature.feature?.code || feature.id;
              const isExpanded = expandedModules[moduleKey] || false;

              return (
                <Box key={feature.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircle sx={{ fontSize: 14, color: "#7B61FF", mt: "3px" }} />
                    <Typography variant="caption" fontWeight={600} color="text.primary">
                      {formatFeatureName(feature.feature?.name)}
                    </Typography>
                    {hasFeatures && (
                      <Box display="flex" gap={0.5} ml={0.5}>
                        {/* <Chip label={`${totalIncluded}/${totalChildren} included`} size="small" sx={{ backgroundColor: "#E8F5E9", color: "#2E7D32", fontWeight: 500, fontSize: 10, height: 18 }} /> */}
                        {totalExcluded > 0 && (
                          <Chip
                            label={`${totalExcluded}/${totalChildren} excluded`}
                            size="small"
                            sx={{ backgroundColor: "#FDECEA", color: "#B71C1C", fontWeight: 500, fontSize: 10, height: 18 }}
                          />
                        )}
                        {totalHidden > 0 && (
                          <Chip
                            label={`${totalHidden}/${totalChildren} locked`}
                            size="small"
                            sx={{ backgroundColor: "#E8F5E9", color: "#2E7D32", fontWeight: 500, fontSize: 10, height: 18 }}
                          />
                        )}
                      </Box>
                    )}
                    {hasFeatures && (
                      <IconButton
                        size="small"
                        onClick={() => setExpandedModules((prev) => ({ ...prev, [moduleKey]: !isExpanded }))}
                        sx={{ p: 0.25 }}
                      >
                        {isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                      </IconButton>
                    )}
                  </Box>
                  {hasFeatures && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box ml={3} display="flex" flexDirection="column" gap={0.5}>
                        {includedChildren.length > 0 &&
                          includedChildren.map((child) => (
                            <Box
                              key={child.code}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                width: "fit-content",
                              }}
                            >
                              <CheckCircle sx={{ fontSize: 11, color: "#4CAF50" }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatFeatureName(child.name)}
                              </Typography>
                            </Box>
                          ))}
                        {hiddenChildren.length > 0 &&
                          hiddenChildren.map((child) => (
                            <Box
                              key={child.code}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                backgroundColor: "#E8F5E9",
                                borderRadius: 1,
                                px: 1,
                                py: 0.25,
                                width: "fit-content",
                              }}
                            >
                              <Lock sx={{ fontSize: 11, color: "#2E7D32" }} />
                              <Typography variant="caption" sx={{ color: "#2E7D32", fontWeight: 500 }}>
                                {formatFeatureName(child.name)}
                              </Typography>
                            </Box>
                          ))}
                        {excludedChildren.length > 0 &&
                          excludedChildren.map((child) => (
                            <Box
                              key={child.code}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                backgroundColor: "#FDECEA",
                                borderRadius: 1,
                                px: 1,
                                py: 0.25,
                                width: "fit-content",
                              }}
                            >
                              <Cancel sx={{ fontSize: 11, color: "#B71C1C" }} />
                              <Typography
                                variant="caption"
                                sx={{ textDecoration: "line-through", color: "#B71C1C", fontWeight: 500 }}
                              >
                                {formatFeatureName(child.name)}
                              </Typography>
                            </Box>
                          ))}
                        {childFeatures.length === 0 &&
                          includedCodes.length > 0 &&
                          includedCodes.map((code) => (
                            <Box
                              key={code}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                backgroundColor: "#E8F5E9",
                                borderRadius: 1,
                                px: 1,
                                py: 0.25,
                                width: "fit-content",
                              }}
                            >
                              <CheckCircle sx={{ fontSize: 11, color: "#4CAF50" }} />
                              <Typography variant="caption" sx={{ color: "#2E7D32", fontWeight: 500 }}>
                                {formatFeatureName(code)}
                              </Typography>
                            </Box>
                          ))}
                        {childFeatures.length === 0 &&
                          hiddenCodes.length > 0 &&
                          hiddenCodes.map((code) => (
                            <Box
                              key={code}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                backgroundColor: "#E8F5E9",
                                borderRadius: 1,
                                px: 1,
                                py: 0.25,
                                width: "fit-content",
                              }}
                            >
                              <Lock sx={{ fontSize: 11, color: "#2E7D32" }} />
                              <Typography variant="caption" sx={{ color: "#2E7D32", fontWeight: 500 }}>
                                {formatFeatureName(code)}
                              </Typography>
                            </Box>
                          ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}

            {inactiveModules.map((feature) => (
              <Box key={feature.id} display="flex" alignItems="center" gap={1}>
                <RemoveCircle sx={{ fontSize: 14, color: "#FF0000", mt: "3px" }} />
                <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                  {formatFeatureName(feature.feature?.name)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <ActionButton buttonText="Update Plan" onButtonClick={onClick} variant="outlined" size="small" sx={{ mt: 22 }} />
      </Stack>
    </CardLayout>
  );
};
